/* eslint-disable react/display-name */
/*
 * Copyright © 2020-2024 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: caoran
 * @LastEditTime: 2025-02-14 11:49:39
 */
import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle
} from 'react'
import {
  Modal,
  Input,
  Button,
  Tree,
  Checkbox,
  Dropdown,
  Form,
  Space
} from 'antd'
import type { MenuProps } from 'antd'
import {
  CaretDownOutlined,
  CloseOutlined,
  EditOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { produce } from 'immer'
import { TodayIconFont as IconFont } from '../components/IconFonts'
import {
  useAppSelector,
  useAppDispatch,
  useVip,
  usePayment,
  useSensorsRole
} from '../../app/hooks'
import {
  Department,
  DepartmentV2,
  DepartmentMember,
  DepartmentMemberV2
} from '../../global'
import sensors from '../../utils/sensors'
import { InputLimitByteLength } from '../components/inputLimitByteLength'
import { tCaptcha } from '../../utils/utils'
import crown_btn_icon from '../../images/btn-crown-icon.png'
import { exportUsers } from '../../actions/group'
import DownloadHistory from './DownloadHistory'
// antd 使用 items 属性代替 Menu.Item/SubMenu
import { useDepartment } from './hooks'
import CONFIG from '../../constants/config'

const { Search } = Input
const { TreeNode } = Tree
const FormItem = Form.Item

interface IDepartmentNameProps {
  onDelete: () => void
  onRename: (departmentName: string) => Promise<any>
  onNew: (departmentName: string) => Promise<any>
}

export const DepartmentName: React.FC<IDepartmentNameProps> = (props) => {
  const { onDelete, onRename, onNew } = props

  const [renameModalVisible, setRenameModalVisible] = useState(false)
  const [newDepartmentModalVisible, setNewDepartmentModalVisible] =
    useState(false)
  const [departmentName, setDepartmentName] = useState('')
  const [newDepartmentName, setNewDepartmentName] = useState('')
  const [isGroup, setIsGroup] = useState(false)
  const [downloadHistoryVisible, setDownloadHistoryVisible] = useState(false)

  const curDepartment = useAppSelector(
    (state) => state.organization.curDepartment
  ) as Department | null
  const groupID = useAppSelector((state) => state.group.groupId)
  const { isVip, isSVip } = useVip()
  const payment = usePayment()
  const dispatch = useAppDispatch()

  useEffect(() => {
    if (curDepartment) {
      setDepartmentName(curDepartment.name)
      setIsGroup(curDepartment.id === '0')
    }
  }, [curDepartment, renameModalVisible])

  useEffect(() => {
    setNewDepartmentName('')
  }, [newDepartmentModalVisible, newDepartmentModalVisible])

  const onRenameSubmit = () => {
    onRename(departmentName).then(() => {
      setRenameModalVisible(false)
      setDepartmentName('')
    })
  }

  const onNewClick = () => {
    sensors.track('web_click_page_workgroup_groupcontacts', {
      clickItem: 'cretaeDepartment',
      groupID: groupID
    })
    setNewDepartmentModalVisible(true)

    // if (isSVip) {
    //   setNewDepartmentModalVisible(true)
    // } else {
    //   payment.openSvip('webGroupDepartmentNum')
    // }
  }

  const onNewSubmit = () => {
    onNew(newDepartmentName).then(() => {
      setNewDepartmentModalVisible(false)
      setNewDepartmentName('')
    })
  }

  const onSetDepartmentName = (value) => {
    setDepartmentName(value)
  }

  const onSetNewDepartmentName = (value) => {
    setNewDepartmentName(value)
  }

  return (
    <>
      <div className="title-wrap">
        <div className="title">
          <p>{curDepartment && curDepartment.name}</p>
          {!isGroup && (
            <EditOutlined
              onClick={() => {
                sensors.track('web_click_page_workgroup_groupcontacts', {
                  clickItem: 'changeDepartmentName',
                  groupID: groupID
                })
                setRenameModalVisible(true)
              }}
            />
          )}

          <Modal
            title="修改部门名称"
            open={renameModalVisible}
            okText="保存"
            onCancel={() => setRenameModalVisible(false)}
            onOk={onRenameSubmit}
            okButtonProps={{
              disabled: departmentName.trim() === ''
            }}
          >
            <InputLimitByteLength
              value={departmentName}
              maxLength={40}
              onChange={onSetDepartmentName}
            />
            <p style={{ marginTop: '16px' }}>请输入部门名称，最多20个汉字</p>
          </Modal>
        </div>
        <div className="actions">
          <Button type="primary" onClick={onNewClick}>
            添加子部门
          </Button>
          {curDepartment && curDepartment.id !== '0' && (
            <Button danger ghost onClick={onDelete}>
              删除部门
            </Button>
          )}

          <Button
            type="primary"
            ghost
            onClick={() => {
              sensors.track('web_click_page_workgroup_groupcontacts', {
                clickItem: 'exportMembers',
                groupID: groupID
              })

              dispatch(exportUsers({ groupID })).then((res) => {
                setDownloadHistoryVisible(true)
              })
            }}
          >
            导出名单
          </Button>

          <Button
            type="link"
            style={{ padding: 0 }}
            onClick={() => {
              setDownloadHistoryVisible(true)
            }}
          >
            下载记录
          </Button>

          <Modal
            title="添加子部门"
            open={newDepartmentModalVisible}
            okText="保存"
            onCancel={() => setNewDepartmentModalVisible(false)}
            onOk={onNewSubmit}
            okButtonProps={{
              disabled: newDepartmentName.trim() === ''
            }}
          >
            <InputLimitByteLength
              value={newDepartmentName}
              maxLength={40}
              onChange={onSetNewDepartmentName}
            />
            <p style={{ marginTop: '16px' }}>请输入部门名称，最多20个汉字</p>
          </Modal>
        </div>
      </div>

      <DownloadHistory
        visible={downloadHistoryVisible}
        onClose={() => setDownloadHistoryVisible(false)}
      />
    </>
  )
}

export type NewUser = {
  userName: string
  userMobile: string
}

interface IActionBarProps {
  onSetBtnClick: () => void
  onAddBtnClick: () => void
  onBatchDeleteBtnClick: () => void
  onSearch: (value: string) => void
  onAddMember: (newUser: NewUser, res: any) => Promise<any>
}

export const ActionsBar: React.FC<IActionBarProps> = (props) => {
  const {
    onSetBtnClick,
    onAddBtnClick,
    onBatchDeleteBtnClick,
    onSearch,
    onAddMember
  } = props

  const navigate = useNavigate()
  const [searchValue, setSearchValue] = useState<string>('')
  const [addMemberModalVisible, setAddMemberModalVisible] =
    useState<boolean>(false)
  const [newMember, setNewMember] = useState<NewUser>({
    userName: '',
    userMobile: ''
  })

  const curDepartment = useAppSelector(
    (state) => state.organization.curDepartment
  ) as any
  const groupID = useAppSelector((state) => state.group.groupId)
  const myRole = useAppSelector((state) => state.group.myRole)
  const groupSize = useAppSelector((state) => state.group.groupSize) || 0
  const groupSizeLimit =
    useAppSelector((state) => state.group.groupSizeLimit) || 0
  const dispatch = useAppDispatch()

  const { isSVip } = useVip()

  const { openSvip } = usePayment()

  const onMenuClick = (action: string) => {
    if (!isSVip && curDepartment.id !== '0') {
      Modal.confirm({
        title: '请开通团队超级会员，再使用部门管理功能',
        icon: <ExclamationCircleOutlined />,
        cancelText: '取消',
        okText: '去开通',
        okButtonProps: {
          style: {
            background: 'linear-gradient(90deg, #F5E3C7 0%, #EABE79 100%)',
            color: '#87563B',
            border: 'none',
            boxShadow: 'none',
            textShadow: 'none'
          }
        },
        onOk: () => {
          openSvip('webGroupDepartmentNumInvite')
        }
      })
    } else {
      if (action === 'web_workgroup_membermanagement_addmember') {
        sensors.track(action)
        setAddMemberModalVisible(true)
      } else if (action === 'web_workgroup_membermanagement_addmember_Import') {
        sensors.track(action)
        navigate(
          `/team/member/import?from=input&departmentID=${curDepartment.id}`
        )
      } else if (action === 'web_workgroup_membermanagement_addmember_Excel') {
        sensors.track(action, {
          clickItem: 'excel'
        })
        navigate(
          `/team/member/import?from=excel&departmentID=${curDepartment.id}`
        )
      }
    }
  }

  const onAddMemberOk = () => {
    if (groupSize >= groupSizeLimit) {
      Modal.warning({
        title: '邀请成员失败',
        content: `团队成员已达到上限${groupSizeLimit}人，您可以清理已离职的成员，或联系客服咨询商务合作。`
      })
      sensors.track('web_get_action_pop_member300_limit', {
        isGroupVIP: isSVip,
        groupID: groupID,
        role: CONFIG.saKeywords.roles[myRole],
        actionType: 'showPop'
      })
      return
    }
    tCaptcha().then((res) => {
      console.log('res', res)
      onAddMember(newMember, res).then(() => {
        setNewMember({ userName: '', userMobile: '' })
        setAddMemberModalVisible(false)
      })
    })
  }

  // antd 使用 items 属性代替 Menu.Item/SubMenu
  const menuItems: MenuProps['items'] = [
    {
      key: 'phone',
      label: '手机号邀请',
      onClick: () => onMenuClick('web_workgroup_membermanagement_addmember')
    },
    {
      key: 'batch',
      label: '批量邀请',
      onClick: () =>
        onMenuClick('web_workgroup_membermanagement_addmember_Import')
    },
    {
      key: 'excel',
      label: 'excel导入',
      children: [
        {
          key: 'excel-department',
          label: '部门成员导入',
          onClick: () =>
            onMenuClick('web_workgroup_membermanagement_addmember_Excel')
        },
        {
          key: 'excel-org',
          label: '组织架构成员导入',
          onClick: () => {
            sensors.track('web_workgroup_membermanagement_addmember_Excel', {
              clickItem: 'organization'
            })
            navigate(`/team/member/org-arc-import`)
          }
        }
      ]
    }
  ]

  useEffect(() => {
    setSearchValue('')
  }, [curDepartment])

  useEffect(() => {
    if (!addMemberModalVisible) {
      setNewMember({ userName: '', userMobile: '' })
    }
  }, [addMemberModalVisible])

  return (
    <div className="actions-bar">
      <div className="actions">
        <Button onClick={onAddBtnClick}>添加成员</Button>
        <Button onClick={onSetBtnClick}>调整部门</Button>
        <Button
          onClick={() => {
            sensors.track('web_click_page_workgroup_groupcontacts', {
              clickItem: 'batchDeleteMember',
              groupID: groupID
            })
            onBatchDeleteBtnClick()
          }}
          style={{ color: '#ff4d4f', borderColor: '#ff4d4f' }}
        >
          批量删除
        </Button>
        <Dropdown menu={{ items: menuItems }} placement="bottom">
          <Button>
            邀请外部成员
            <CaretDownOutlined />
          </Button>
        </Dropdown>
      </div>
      <Search
        allowClear
        enterButton
        placeholder="部门内搜索成员/手机号"
        style={{ width: '260px' }}
        value={searchValue}
        onChange={(e) => setSearchValue(e.target.value)}
        onSearch={onSearch}
      />
      <Modal
        title="手机号邀请成员"
        open={addMemberModalVisible}
        onCancel={() => setAddMemberModalVisible(false)}
        onOk={onAddMemberOk}
        okButtonProps={{
          disabled: !newMember.userName || newMember.userMobile.length !== 11
        }}
      >
        <Form
          layout="horizontal"
          labelCol={{ span: 4 }}
          wrapperCol={{ span: 20 }}
        >
          <FormItem label="姓名">
            <Input
              maxLength={10}
              value={newMember.userName}
              onChange={(e) =>
                setNewMember({ ...newMember, userName: e.target.value.trim() })
              }
            />
          </FormItem>
          <FormItem label="手机号">
            <Space.Compact block>
              <Input
                readOnly
                tabIndex={-1}
                value="+86"
                style={{
                  width: 48,
                  flex: 'none',
                  textAlign: 'center',
                  background: 'rgba(0,0,0,0.02)',
                  color: 'rgba(0,0,0,0.88)',
                  cursor: 'default',
                  pointerEvents: 'none'
                }}
              />
              <Input
                placeholder="请输入11位手机号"
                maxLength={11}
                value={newMember.userMobile}
                onChange={(e) =>
                  setNewMember({
                    ...newMember,
                    userMobile: e.target.value.trim()
                  })
                }
              />
            </Space.Compact>
          </FormItem>
        </Form>
        <p style={{ marginLeft: '75px' }}>
          该成员将收到短信邀请，同意后即可加入团队
        </p>
      </Modal>
    </div>
  )
}

interface ISetDepartmentModalProps {
  visible: boolean
  onCancel: () => void
  onOk: (departmentList: string[]) => Promise<any>
}

interface IGenerateTreeNodes {
  (data: Department | Department[]): any
}

interface ISelectedDepartment {
  id: string
  name: string
}

export const SetDepartmentModal: React.FC<ISetDepartmentModalProps> = (
  props
) => {
  const { visible, onCancel, onOk } = props

  const [selectedDepartments, setSelectedDepartments] = useState<
    ISelectedDepartment[]
  >([])

  const organization = useAppSelector(
    (state) => state.organization.organization
  )

  useEffect(() => {
    setSelectedDepartments([])
  }, [visible])

  const generateTreeNodes: IGenerateTreeNodes = (data) => {
    if (data) {
      if (Array.isArray(data)) {
        return data.map((department) => {
          const selected = selectedDepartments.some(
            (selectedDepartment) => selectedDepartment.id === department.id
          )
          const title = (
            <span className="treenode-title">
              <span className="name">{department.name}</span>
              <Checkbox
                checked={selected}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedDepartments(
                      produce(selectedDepartments, (draft) => {
                        if (!selected) {
                          draft.push({
                            id: department.id,
                            name: department.name
                          })
                        }
                      })
                    )
                  } else {
                    setSelectedDepartments(
                      produce(selectedDepartments, (draft) => {
                        draft.splice(
                          draft.findIndex(
                            (selectedDepartment) =>
                              selectedDepartment.id === department.id
                          ),
                          1
                        )
                      })
                    )
                  }
                }}
              />
            </span>
          )
          return (
            <TreeNode
              icon={
                <IconFont type="icondepartment" style={{ color: '#0093FF' }} />
              }
              title={title}
              key={department.id}
            >
              {department.children.length > 0 &&
                generateTreeNodes(department.children)}
            </TreeNode>
          )
        })
      } else {
        const selected = selectedDepartments.some(
          (selectedDepartment) => selectedDepartment.id === data.id
        )
        const title = (
          <span className="treenode-title">
            <span className="name">{data.name}</span>
            <Checkbox
              checked={selected}
              onChange={(e) => {
                if (e.target.checked) {
                  setSelectedDepartments(
                    produce(selectedDepartments, (draft) => {
                      if (!selected) {
                        draft.push({ id: data.id, name: data.name })
                      }
                    })
                  )
                } else {
                  setSelectedDepartments(
                    produce(selectedDepartments, (draft) => {
                      draft.splice(
                        draft.findIndex(
                          (selectedDepartment) =>
                            selectedDepartment.id === data.id
                        ),
                        1
                      )
                    })
                  )
                }
              }}
            />
          </span>
        )
        return (
          <TreeNode
            icon={
              <IconFont type="icondepartment" style={{ color: '#0093FF' }} />
            }
            title={title}
            key={data.id}
          >
            {data.children.length > 0 && generateTreeNodes(data.children)}
          </TreeNode>
        )
      }
    }
  }

  const renderSelectedDepartments = () => {
    return selectedDepartments.map((department) => {
      return (
        <div className="selected-department" key={department.id}>
          <div className="department-name">
            <IconFont type="icondepartment" style={{ color: '#0093FF' }} />
            <span className="name">{department.name}</span>
          </div>
          <CloseOutlined
            style={{ cursor: 'pointer' }}
            onClick={() => {
              setSelectedDepartments(
                produce(selectedDepartments, (draft) => {
                  draft.splice(
                    draft.findIndex((item) => item.id === department.id),
                    1
                  )
                })
              )
            }}
          />
        </div>
      )
    })
  }

  const onSetOk = () => {
    const departmentList: string[] = selectedDepartments.map(
      (department) => department.id
    )
    onOk(departmentList).then(() => setSelectedDepartments([]))
  }

  return (
    <Modal
      open={visible}
      getContainer={false}
      title="调整部门"
      width={600}
      styles={{ body: { height: '500px' } }}
      onCancel={onCancel}
      onOk={onSetOk}
      okButtonProps={{
        disabled: selectedDepartments.length === 0
      }}
    >
      <div className="set-department-modal">
        <div className="source">
          <h3>选择</h3>
          <div className="department-container">
            {organization && (
              <Tree //为了让defaultExpandedKeys生效，需要有数据后再渲染树，否则树从无到有进行更新，defaultExpandedKeys将不生效
                showIcon
                blockNode
                multiple={false}
                selectable={false}
                defaultExpandedKeys={['0']}
                switcherIcon={
                  <CaretDownOutlined style={{ fontSize: '14px' }} />
                }
              >
                {generateTreeNodes(organization)}
              </Tree>
            )}
          </div>
        </div>
        <div className="target">
          <h3>移动至</h3>
          <div className="department-container">
            {renderSelectedDepartments()}
          </div>
        </div>
      </div>
    </Modal>
  )
}

interface IAddMembersModalProps {
  visible: boolean
  onCancel: () => void
  onOk: (departmentUserList: string[], departmentList: string[]) => Promise<any>
}

interface ISelectedDepartment {
  id: string
  name: string
}

/**
 *
 * @param props
 * @returns
 * 不可选的成员/部门：
 ● 当前部门；
 ● 当前部门的所有父级部门；
 ● 当前部门的直接下级部门；
 ● 当前部门的直接下级成员。
 */
export const AddMembersModal: React.FC<IAddMembersModalProps> = (props) => {
  const { visible, onCancel, onOk } = props

  const [selectedDepartments, setSelectedDepartments] = useState<
    ISelectedDepartment[]
  >([])
  const [selectedUsers, setSelectedUsers] = useState<DepartmentMemberV2[]>([])
  //从组织架构中选择的成员列表
  // const [selectedDepartmentUsers, setSelectedDepartmentUsers] = useState<DepartmentMember[]>([])

  const organization = useAppSelector(
    (state) => state.organization.organization
  )
  const curDepartment = useAppSelector(
    (state) => state.organization.curDepartment
  )
  const disabledParentDepartmentIDs = useAppSelector(
    (state) => state.organization.parentDepartmentIDs
  )

  const { findUsersByDepartmentIds } = useDepartment()

  const { isSVip } = useVip()

  const { openSvip } = usePayment()

  const childrenDepartmentIDs = curDepartment
    ? curDepartment.children.map((item) => item.id)
    : []

  /**
   * 取消选择成员时，从2个已选成员列表中移除此成员
   * @param userID
   */
  const removeUserFromSeleteUsers = (userID: string): void => {
    const selectedDepartmentUsersIndex = selectedUsers.findIndex(
      (selectedDepartmentUser) => selectedDepartmentUser.userID === userID
    )
    if (selectedDepartmentUsersIndex > -1) {
      setSelectedUsers(
        produce(selectedUsers, (draft) => {
          draft.splice(selectedDepartmentUsersIndex, 1)
        })
      )
    }
  }

  /**
   * 创建组织架构下的成员节点列表
   * @param users 要生成的成员节点
   * @param checkboxDisabled 如果父级部门已选中，则成员节点也选中，并且禁止操作
   * @returns 成员节点列表
   */
  const generateUserNodes = (
    users: DepartmentMemberV2[],
    parentChecked: boolean
  ) => {
    return users
      .filter((user) => !user.quit)
      .map((user) => {
        const selected =
          selectedUsers.some((selectedUser) => selectedUser.id === user.id) ||
          parentChecked
        const childrenUserIDs =
          curDepartment?.users?.map((user: any) => user.departmentUserID) || []
        const disabled = childrenUserIDs.includes(user.id)
        const title = (
          <span className="treenode-title">
            <span className="name">{user.name}</span>
            <Checkbox
              checked={selected} //如果父级部门已选中，则成员节点也选中，并且禁止操作
              disabled={selected || disabled} //如果父级部门已选中，则成员节点也选中，并且禁止操作
              onChange={(e) => {
                if (e.target.checked) {
                  setSelectedUsers(
                    produce(selectedUsers, (draft) => {
                      if (!selected) {
                        draft.push(user)
                      }
                    })
                  )
                } else {
                  removeUserFromSeleteUsers(user.userID)
                }
              }}
            />
          </span>
        )
        return (
          <TreeNode
            icon={<img className="headimg" src={user.avatar} />}
            title={title}
            key={user.userID}
          />
        )
      })
  }

  /**
   *
   * @param departments 要生成的组织架构树
   * @param parentChecked 如果父级已选中，则子孙节点也选中并禁止操作
   * @returns
   */
  const generateTreeNodes = (
    departments: DepartmentV2[],
    parentChecked: boolean
  ): any => {
    return departments.map((department) => {
      const selected =
        selectedDepartments.some(
          (selectedDepartment) => selectedDepartment.id === department.id
        ) || parentChecked
      const disabled =
        disabledParentDepartmentIDs.includes(department.id) ||
        curDepartment?.id === department.id ||
        childrenDepartmentIDs.includes(department.id)
      const title = (
        <span className="treenode-title">
          <span className="name">{department.name}</span>
          <Checkbox
            checked={selected} //如果父级已选中，则子节点也选中并禁止操作
            disabled={selected || disabled} //如果父级已选中，则子节点也选中并禁止操作
            onChange={(e) => {
              if (e.target.checked) {
                //选中部门
                //检查已选择部门列表中是否包含子孙级节点，如果存在则过滤掉子孙级节点，只保留当前部门节点
                setSelectedDepartments(
                  produce(selectedDepartments, (draft) => {
                    const departmentChildIDs =
                      findChildrenDepartmentIDs(department)
                    const mDraft = draft.filter(
                      (selectedDepartment) =>
                        !departmentChildIDs.includes(selectedDepartment.id)
                    )
                    if (!selected) {
                      mDraft.push({ id: department.id, name: department.name })
                    }
                    return mDraft
                  })
                )
                //检查已选择部门成员列表中是否包含此部门下的成员，如果存在则过滤掉，只保留当前部门节点
                setSelectedUsers(
                  produce(selectedUsers, (draft) => {
                    const userIDs = findUserIDsInDepartment(department)
                    const mDraft = draft.filter(
                      (selectedDepartmentUser) =>
                        !userIDs.includes(selectedDepartmentUser.userID)
                    )
                    return mDraft
                  })
                )
              } else {
                setSelectedDepartments(
                  produce(selectedDepartments, (draft) => {
                    draft.splice(
                      draft.findIndex(
                        (selectedDepartment) =>
                          selectedDepartment.id === department.id
                      ),
                      1
                    )
                  })
                )
              }
            }}
          />
        </span>
      )
      return (
        <TreeNode
          icon={<IconFont type="icondepartment" style={{ color: '#0093FF' }} />}
          title={title}
          key={department.id}
        >
          {department.children &&
            department.children.length > 0 &&
            generateTreeNodes(department.children, selected)}
          {department.users &&
            department.users.length > 0 &&
            generateUserNodes(department.users, selected)}
        </TreeNode>
      )
    })
  }

  const renderSelectedDepartments = () => {
    const departments = selectedDepartments.map((department) => {
      return (
        <div className="selected-department" key={department.id}>
          <div>
            <IconFont type="icondepartment" style={{ color: '#0093FF' }} />
            <span>{department.name}</span>
          </div>
          <CloseOutlined
            style={{ cursor: 'pointer' }}
            onClick={() => {
              setSelectedDepartments(
                produce(selectedDepartments, (draft) => {
                  draft.splice(
                    draft.findIndex((item) => item.id === department.id),
                    1
                  )
                })
              )
            }}
          />
        </div>
      )
    })
    const users = selectedUsers.map((user) => {
      return (
        <div className="selected-department" key={user.id}>
          <div className="user-name">
            <img className="headimg" src={user.avatar} />
            <span>{user.name}</span>
          </div>
          <CloseOutlined
            style={{ cursor: 'pointer' }}
            onClick={() => {
              setSelectedUsers(
                produce(selectedUsers, (draft) => {
                  draft.splice(
                    draft.findIndex((item) => item.id === user.id),
                    1
                  )
                })
              )
            }}
          />
        </div>
      )
    })
    return (
      <>
        {departments}
        {users}
      </>
    )
  }

  const onSetOkClick = () => {
    if (isSVip) {
      onSetOk()
    } else {
      openSvip('webGroupDepartmentNumAdd')
    }
  }

  const onSetOk = () => {
    const departmentList: string[] = selectedDepartments.map(
      (department) => department.id
    )
    const departmentUserList: string[] = selectedUsers.map((user) => user.id)

    // 选择部门下的所有成员
    const allDepartmentUsers = findUsersByDepartmentIds(departmentList)
    //如果选择的成员中有其他部门管理员，则需要提示
    if (
      selectedUsers.some(
        (user) => user.role === CONFIG.memberRoleTypes.DEPARTMENT_ADMIN
      ) ||
      allDepartmentUsers.some(
        (user) => user.role === CONFIG.memberRoleTypes.DEPARTMENT_ADMIN
      )
    ) {
      Modal.confirm({
        title: '提示',
        content: '添加的成员，如果为其他部门管理员，会自动成为新部门管理员',
        onOk: () => {
          onOk(departmentUserList, departmentList).then(() => {
            setSelectedDepartments([])
            setSelectedUsers([])
          })
        }
      })
    } else {
      onOk(departmentUserList, departmentList).then(() => {
        setSelectedDepartments([])
        setSelectedUsers([])
      })
    }
  }

  useEffect(() => {
    setSelectedUsers([])
    setSelectedDepartments([])
  }, [visible, curDepartment])

  return (
    <Modal
      open={visible}
      getContainer={false}
      title="添加成员"
      width={600}
      styles={{ body: { height: '500px' } }}
      mask={{ closable: false }}
      onCancel={onCancel}
      footer={[
        <Button key="back" onClick={onCancel}>
          取消
        </Button>,
        <Button
          key="submit"
          type="primary"
          style={{ display: 'inline-flex', alignItems: 'center' }}
          onClick={onSetOkClick}
        >
          确定
          <img
            style={{ width: 20, marginLeft: 4 }}
            src={crown_btn_icon}
            alt="crown"
          />
        </Button>
      ]}
    >
      <div className="set-department-modal">
        <div className="source">
          <h3>选择</h3>
          <div className="department-container">
            {organization && curDepartment && (
              <Tree //为了让defaultExpandedKeys生效，需要有数据后再渲染树，否则树从无到有进行更新，defaultExpandedKeys将不生效
                showIcon
                blockNode
                multiple={false}
                selectable={false}
                defaultExpandedKeys={['0']}
                switcherIcon={
                  <CaretDownOutlined style={{ fontSize: '14px' }} />
                }
              >
                {generateTreeNodes([organization], false)}
              </Tree>
            )}
          </div>
        </div>
        <div className="target">
          <h3>已选</h3>
          <div className="department-container">
            {renderSelectedDepartments()}
          </div>
        </div>
      </div>
    </Modal>
  )
}

interface IRenameModalProps {
  visible: boolean
  defaultName: string
  onCancel: () => void
  onOk: (newName: string) => void
}

export const RenameModal: React.FC<IRenameModalProps> = (props) => {
  const [newName, setNewName] = useState<string>('')
  const { visible, onCancel, defaultName } = props

  useEffect(() => {
    setNewName(props.defaultName)
  }, [defaultName, visible])

  const onOk = () => {
    props.onOk(newName)
  }

  const onEditName = (value) => {
    setNewName(value)
  }

  return (
    <Modal
      title="修改姓名"
      open={visible}
      onCancel={onCancel}
      okButtonProps={{ disabled: !newName.trim() }}
      onOk={onOk}
    >
      <InputLimitByteLength
        value={newName}
        maxLength={20}
        onChange={onEditName}
      />
      <p style={{ marginTop: '10px' }}>
        请填写团队成员的真实姓名，最多10个汉字
      </p>
    </Modal>
  )
}

/**
 *
 * @param department 部门
 * @returns 部门所有子孙节点ID列表
 */
export const findChildrenDepartmentIDs = (
  department: DepartmentV2
): string[] => {
  let ids = department.children?.map((childDepartment) => childDepartment.id)
  const findIDsRecursive = (children: DepartmentV2[]) => {
    for (let index = 0; index < children.length; index++) {
      const element = children[index]
      ids = (ids ?? []).concat(
        element.children?.map((childDepartment) => childDepartment.id) || []
      )
      findIDsRecursive(element.children || [])
    }
  }
  findIDsRecursive(department.children || [])
  return ids || []
}

/**
 *
 * @param department 部门
 * @returns 部门和所有子孙节点下userID列表
 */
export const findUserIDsInDepartment = (department: DepartmentV2): string[] => {
  let ids = department.users?.map((user) => user.userID)
  const findIDsRecursive = (children: DepartmentV2[]) => {
    for (let index = 0; index < children.length; index++) {
      const element = children[index]
      ids = (ids ?? []).concat(element.users?.map((user) => user.userID) || [])
      findIDsRecursive(element.children || [])
    }
  }
  findIDsRecursive(department.children || [])
  return ids || []
}

interface GlobalSearchBarProps {
  onSearch: (value: string) => void
}

export const GlobalSearchBar = forwardRef(
  (props: GlobalSearchBarProps, ref) => {
    const { onSearch } = props
    const [searchValue, setSearchValue] = useState<string>('')
    const groupID: string = useAppSelector((state) => state.group.groupId)
    const role = useSensorsRole()

    useImperativeHandle(ref, () => ({
      onClear
    }))

    const onClear = () => {
      setSearchValue('')
    }

    const handleSearch = (value) => {
      sensors.track('web_click_page_workgroup_groupcontacts', {
        clickItem: 'globalSearch',
        groupID: groupID,
        role
      })
      onSearch(value)
    }

    return (
      <Search
        allowClear
        enterButton
        placeholder="搜索成员姓名/手机号"
        style={{ width: '400px' }}
        value={searchValue}
        onChange={(e) => setSearchValue(e.target.value)}
        onSearch={handleSearch}
      />
    )
  }
)
